import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createTextVNode as _createTextVNode, unref as _unref, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, Transition as _Transition, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "portal-header__top" }
const _hoisted_2 = { class: "portal-header__top-inner" }
const _hoisted_3 = { class: "portal-header__brand" }
const _hoisted_4 = { class: "portal-header__logo-line" }
const _hoisted_5 = {
  class: "portal-header__nav",
  "aria-label": "주요 메뉴"
}
const _hoisted_6 = ["aria-expanded", "title"]
const _hoisted_7 = { class: "portal-header__welcome" }
const _hoisted_8 = {
  key: 0,
  class: "portal-header__panel portal-header__panel--right"
}
const _hoisted_9 = { class: "portal-header__who" }
const _hoisted_10 = ["aria-expanded", "title"]
const _hoisted_11 = { class: "portal-header__welcome" }
const _hoisted_12 = {
  key: 0,
  class: "portal-header__panel portal-header__panel--right"
}
const _hoisted_13 = { class: "portal-header__who" }
const _hoisted_14 = ["aria-expanded", "title"]
const _hoisted_15 = { class: "portal-header__welcome" }
const _hoisted_16 = {
  key: 0,
  class: "portal-header__panel portal-header__panel--right"
}
const _hoisted_17 = { class: "portal-header__who" }
const _hoisted_18 = {
  key: 0,
  class: "portal-drawer",
  "aria-label": "전체 메뉴"
}
const _hoisted_19 = { class: "portal-drawer__head" }
const _hoisted_20 = { class: "portal-drawer__nav" }
const _hoisted_21 = { class: "portal-drawer__foot" }

import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import { MALL_HOME_PATH } from '@/constants/mall'
import { goPortalLogin } from '@/composables/useLoginDestination'
import { useSellerAuth } from '@/composables/useSellerAuth'
import { useSellerCenter } from '@/composables/useSellerCenter'
import { staffManagePath } from '@/composables/useUnifiedLogin'
import { useAdminAuth } from '@/composables/useAdminAuth'
import { useMemberAuth } from '@/composables/useMemberAuth'

const logoHomeTo = '/'

export default /*@__PURE__*/_defineComponent({
  __name: 'PortalHeader',
  setup(__props) {

const route = useRoute()
const router = useRouter()
const { isLoggedIn, member, logout, initMemberAuth } = useMemberAuth()
const { admin, isAdminLoggedIn, initAdminAuth, logout: logoutAdmin } = useAdminAuth()
const { seller, refreshSellerAuth } = useSellerCenter()
const { isSellerLoggedIn, logout: logoutSeller } = useSellerAuth()
const managePageTo = computed(() =>
  staffManagePath({
    isAdmin: isAdminLoggedIn.value,
    isSeller: isSellerLoggedIn.value,
    seller: seller.value,
  }),
)

const drawerOpen = ref(false)
const userOpen = ref(false)
const isCompact = ref(false)
const utilRef = ref<HTMLElement | null>(null)
let userCloseTimer = 0

function openUser() {
  window.clearTimeout(userCloseTimer)
  userOpen.value = true
}

function closeUserSoon() {
  window.clearTimeout(userCloseTimer)
  userCloseTimer = window.setTimeout(() => {
    userOpen.value = false
  }, 160)
}

const isLanding = computed(() =>
  ['dashboard', 'terms', 'login', 'info-plaza', 'info-detail', 'info-center', 'info-story'].includes(String(route.name ?? '')),
)
const infoActive = computed(
  () => route.path === '/info' || route.path.startsWith('/info/'),
)
const isInfoPage = computed(() => route.name === 'info-center' || route.name === 'info-detail' || route.name === 'info-story')

function onPortalLogin() {
  drawerOpen.value = false
  goPortalLogin(router, route)
}

const memberPersonName = computed(
  () => member.value?.bossName?.trim() || member.value?.userId || '회원',
)
const welcomeName = computed(
  () => member.value?.orgName?.trim() || memberPersonName.value,
)
const sellerPersonName = computed(
  () =>
    seller.value?.ownerName?.trim() ||
    seller.value?.managerName?.trim() ||
    seller.value?.storeName?.trim() ||
    seller.value?.sellerId ||
    '판매업체',
)
const sellerWelcome = computed(
  () => seller.value?.storeName?.trim() || sellerPersonName.value,
)
const adminPersonName = computed(
  () => admin.value?.name?.trim() || admin.value?.userId || '관리자',
)
const adminWelcome = computed(() => adminPersonName.value)

const mallActive = computed(
  () => route.path === MALL_HOME_PATH || route.path.startsWith(`${MALL_HOME_PATH}/`),
)

const onScroll = () => {
  isCompact.value = window.scrollY > 24
}

const onKeydown = (event: KeyboardEvent) => {
  if (event.key !== 'Escape') return
  userOpen.value = false
  drawerOpen.value = false
}

const onDocClick = (event: MouseEvent) => {
  const target = event.target as Node
  if (userOpen.value && utilRef.value && !utilRef.value.contains(target)) {
    userOpen.value = false
  }
}

watch(
  () => route.fullPath,
  () => {
    userOpen.value = false
    drawerOpen.value = false
  },
)

watch(drawerOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
})

onMounted(() => {
  initMemberAuth()
  void initAdminAuth()
  refreshSellerAuth()
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('keydown', onKeydown)
  document.addEventListener('click', onDocClick)
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
  window.removeEventListener('keydown', onKeydown)
  document.removeEventListener('click', onDocClick)
  document.body.style.overflow = ''
  window.clearTimeout(userCloseTimer)
})

async function onLogout() {
  userOpen.value = false
  drawerOpen.value = false
  await logout()
  router.push('/')
}

async function onSellerLogout() {
  userOpen.value = false
  drawerOpen.value = false
  await logoutSeller()
}

async function onAdminLogout() {
  userOpen.value = false
  drawerOpen.value = false
  await logoutAdmin()
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("header", {
    class: _normalizeClass(["portal-header", { 'is-compact': isCompact.value, 'is-landing': isLanding.value, 'is-info': isInfoPage.value }])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("button", {
          type: "button",
          class: "portal-header__burger",
          "aria-label": "전체 메뉴 열기",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (drawerOpen.value = true))
        }, [
          _createVNode(AppIcon, {
            icon: "mdi-menu",
            size: 24
          })
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_component_router_link, {
            to: logoHomeTo,
            class: "portal-header__logo"
          }, {
            default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
              _createElementVNode("img", {
                src: "/favicon.svg",
                width: "34",
                height: "34",
                alt: "",
                "aria-hidden": "true"
              }, null, -1)
            ]))]),
            _: 1
          }),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_component_router_link, {
              to: logoHomeTo,
              class: "portal-header__logo-name"
            }, {
              default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                _createTextVNode(" 장기요양지원센터 ", -1)
              ]))]),
              _: 1
            })
          ])
        ]),
        _createElementVNode("nav", _hoisted_5, [
          (_unref(isAdminLoggedIn))
            ? (_openBlock(), _createBlock(_component_router_link, {
                key: 0,
                to: "/info",
                class: _normalizeClass(["portal-header__menu-link", { 'is-on': infoActive.value }]),
                "aria-current": infoActive.value ? 'page' : undefined
              }, {
                default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                  _createTextVNode(" 정보마당 ", -1)
                ]))]),
                _: 1
              }, 8, ["class", "aria-current"]))
            : _createCommentVNode("", true),
          _createVNode(_component_router_link, {
            to: _unref(MALL_HOME_PATH),
            target: "_blank",
            rel: "noopener noreferrer",
            class: _normalizeClass(["portal-header__menu-link", { 'is-on': mallActive.value }])
          }, {
            default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
              _createTextVNode(" 함우리몰 ", -1)
            ]))]),
            _: 1
          }, 8, ["to", "class"])
        ]),
        _createElementVNode("div", {
          ref_key: "utilRef",
          ref: utilRef,
          class: "portal-header__util"
        }, [
          (_unref(isLoggedIn) && _unref(member))
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "portal-header__util-item",
                onMouseenter: openUser,
                onMouseleave: closeUserSoon
              }, [
                _createElementVNode("button", {
                  type: "button",
                  class: _normalizeClass(["portal-header__profile", { 'is-open': userOpen.value }]),
                  "aria-expanded": userOpen.value,
                  title: memberPersonName.value,
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (userOpen.value = !userOpen.value))
                }, [
                  _createVNode(AppIcon, {
                    icon: "mdi-account-circle",
                    size: 20
                  }),
                  _createElementVNode("span", _hoisted_7, _toDisplayString(welcomeName.value) + "님", 1),
                  _createVNode(AppIcon, {
                    icon: "mdi-chevron-down",
                    size: 16
                  })
                ], 10, _hoisted_6),
                (userOpen.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                      _createElementVNode("p", _hoisted_9, _toDisplayString(memberPersonName.value), 1),
                      _createVNode(_component_router_link, {
                        to: "/my-info",
                        onClick: _cache[2] || (_cache[2] = ($event: any) => (userOpen.value = false))
                      }, {
                        default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                          _createTextVNode("마이페이지", -1)
                        ]))]),
                        _: 1
                      }),
                      _createElementVNode("button", {
                        type: "button",
                        class: "is-logout",
                        onClick: onLogout
                      }, "로그아웃")
                    ]))
                  : _createCommentVNode("", true)
              ], 32))
            : (_unref(isSellerLoggedIn) && _unref(seller))
              ? (_openBlock(), _createElementBlock("div", {
                  key: 1,
                  class: "portal-header__util-item",
                  onMouseenter: openUser,
                  onMouseleave: closeUserSoon
                }, [
                  _createElementVNode("button", {
                    type: "button",
                    class: _normalizeClass(["portal-header__profile", { 'is-open': userOpen.value }]),
                    "aria-expanded": userOpen.value,
                    title: sellerPersonName.value,
                    onClick: _cache[3] || (_cache[3] = ($event: any) => (userOpen.value = !userOpen.value))
                  }, [
                    _createVNode(AppIcon, {
                      icon: "mdi-storefront-outline",
                      size: 20
                    }),
                    _createElementVNode("span", _hoisted_11, _toDisplayString(sellerWelcome.value), 1),
                    _createVNode(AppIcon, {
                      icon: "mdi-chevron-down",
                      size: 16
                    })
                  ], 10, _hoisted_10),
                  (userOpen.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                        _createElementVNode("p", _hoisted_13, _toDisplayString(sellerPersonName.value), 1),
                        _createVNode(_component_router_link, {
                          to: managePageTo.value || '/seller',
                          onClick: _cache[4] || (_cache[4] = ($event: any) => (userOpen.value = false))
                        }, {
                          default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                            _createTextVNode(" 관리자페이지 ", -1)
                          ]))]),
                          _: 1
                        }, 8, ["to"]),
                        _createElementVNode("button", {
                          type: "button",
                          class: "is-logout",
                          onClick: onSellerLogout
                        }, "로그아웃")
                      ]))
                    : _createCommentVNode("", true)
                ], 32))
              : (_unref(isAdminLoggedIn) && _unref(admin))
                ? (_openBlock(), _createElementBlock("div", {
                    key: 2,
                    class: "portal-header__util-item",
                    onMouseenter: openUser,
                    onMouseleave: closeUserSoon
                  }, [
                    _createElementVNode("button", {
                      type: "button",
                      class: _normalizeClass(["portal-header__profile", { 'is-open': userOpen.value }]),
                      "aria-expanded": userOpen.value,
                      title: adminPersonName.value,
                      onClick: _cache[5] || (_cache[5] = ($event: any) => (userOpen.value = !userOpen.value))
                    }, [
                      _createVNode(AppIcon, {
                        icon: "mdi-shield-crown-outline",
                        size: 20
                      }),
                      _createElementVNode("span", _hoisted_15, _toDisplayString(adminWelcome.value) + "님", 1),
                      _createVNode(AppIcon, {
                        icon: "mdi-chevron-down",
                        size: 16
                      })
                    ], 10, _hoisted_14),
                    (userOpen.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                          _createElementVNode("p", _hoisted_17, _toDisplayString(adminPersonName.value), 1),
                          _createVNode(_component_router_link, {
                            to: "/admin",
                            onClick: _cache[6] || (_cache[6] = ($event: any) => (userOpen.value = false))
                          }, {
                            default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                              _createTextVNode("관리자페이지", -1)
                            ]))]),
                            _: 1
                          }),
                          _createElementVNode("button", {
                            type: "button",
                            class: "is-logout",
                            onClick: onAdminLogout
                          }, "로그아웃")
                        ]))
                      : _createCommentVNode("", true)
                  ], 32))
                : (_openBlock(), _createElementBlock("button", {
                    key: 3,
                    type: "button",
                    class: "portal-header__util-link",
                    onClick: onPortalLogin
                  }, " 로그인 "))
        ], 512)
      ])
    ]),
    _createVNode(_Transition, { name: "drawer-fade" }, {
      default: _withCtx(() => [
        (drawerOpen.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "portal-drawer__backdrop",
              onClick: _cache[7] || (_cache[7] = ($event: any) => (drawerOpen.value = false))
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(_Transition, { name: "drawer-slide" }, {
      default: _withCtx(() => [
        (drawerOpen.value)
          ? (_openBlock(), _createElementBlock("aside", _hoisted_18, [
              _createElementVNode("div", _hoisted_19, [
                _cache[21] || (_cache[21] = _createElementVNode("span", { class: "portal-drawer__brand" }, "메뉴", -1)),
                _createElementVNode("button", {
                  type: "button",
                  class: "portal-drawer__close",
                  "aria-label": "닫기",
                  onClick: _cache[8] || (_cache[8] = ($event: any) => (drawerOpen.value = false))
                }, [
                  _createVNode(AppIcon, {
                    icon: "mdi-close",
                    size: 22
                  })
                ])
              ]),
              _createElementVNode("nav", _hoisted_20, [
                (_unref(isAdminLoggedIn))
                  ? (_openBlock(), _createBlock(_component_router_link, {
                      key: 0,
                      to: "/info",
                      class: "portal-drawer__link",
                      onClick: _cache[9] || (_cache[9] = ($event: any) => (drawerOpen.value = false))
                    }, {
                      default: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                        _createTextVNode(" 정보마당 ", -1)
                      ]))]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                _createVNode(_component_router_link, {
                  to: _unref(MALL_HOME_PATH),
                  target: "_blank",
                  rel: "noopener noreferrer",
                  class: "portal-drawer__link",
                  onClick: _cache[10] || (_cache[10] = ($event: any) => (drawerOpen.value = false))
                }, {
                  default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                    _createTextVNode(" 함우리몰 ", -1)
                  ]))]),
                  _: 1
                }, 8, ["to"])
              ]),
              _createElementVNode("div", _hoisted_21, [
                (_unref(isLoggedIn))
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createVNode(_component_router_link, {
                        to: "/my-info",
                        class: "portal-btn portal-btn--secondary",
                        onClick: _cache[11] || (_cache[11] = ($event: any) => (drawerOpen.value = false))
                      }, {
                        default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                          _createTextVNode("마이페이지", -1)
                        ]))]),
                        _: 1
                      }),
                      _createElementVNode("button", {
                        type: "button",
                        class: "portal-btn portal-btn--primary",
                        onClick: onLogout
                      }, "로그아웃")
                    ], 64))
                  : (_unref(isSellerLoggedIn))
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createVNode(_component_router_link, {
                          to: managePageTo.value || '/seller',
                          class: "portal-btn portal-btn--secondary",
                          onClick: _cache[12] || (_cache[12] = ($event: any) => (drawerOpen.value = false))
                        }, {
                          default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                            _createTextVNode("관리자페이지", -1)
                          ]))]),
                          _: 1
                        }, 8, ["to"]),
                        _createElementVNode("button", {
                          type: "button",
                          class: "portal-btn portal-btn--primary",
                          onClick: onSellerLogout
                        }, "로그아웃")
                      ], 64))
                    : (_unref(isAdminLoggedIn))
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                          _createVNode(_component_router_link, {
                            to: "/admin",
                            class: "portal-btn portal-btn--secondary",
                            onClick: _cache[13] || (_cache[13] = ($event: any) => (drawerOpen.value = false))
                          }, {
                            default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
                              _createTextVNode("관리자페이지", -1)
                            ]))]),
                            _: 1
                          }),
                          _createElementVNode("button", {
                            type: "button",
                            class: "portal-btn portal-btn--primary",
                            onClick: onAdminLogout
                          }, "로그아웃")
                        ], 64))
                      : (_openBlock(), _createElementBlock("button", {
                          key: 3,
                          type: "button",
                          class: "portal-btn portal-btn--secondary",
                          onClick: onPortalLogin
                        }, "로그인"))
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 2))
}
}

})